import * as CookieConsent from 'vanilla-cookieconsent'

declare global {
    interface Window {
        avianeseConsent?: { measurementId: string }
        dataLayer: unknown[]
        gtag: (...args: unknown[]) => void
    }
}

function updateConsent() {
    const state = (service: string, category: string) =>
        CookieConsent.acceptedService(service, category) ? 'granted' : 'denied'

    window.gtag('consent', 'update', {
        analytics_storage: state('analytics_storage', 'analytics'),
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        functionality_storage: state('functionality_storage', 'functionality'),
        personalization_storage: state('personalization_storage', 'functionality'),
        security_storage: 'granted',
    })
    window.dataLayer.push({ event: 'avianese_consent_update' })
}

export async function initCookieConsent() {
    if (!window.avianeseConsent) return

    document.documentElement.classList.add('cc--darkmode')
    await CookieConsent.run({
        mode: 'opt-in',
        revision: 3,
        cookie: { expiresAfterDays: 182, sameSite: 'Lax' },
        disablePageInteraction: false,
        onConsent: updateConsent,
        onChange: updateConsent,
        guiOptions: {
            consentModal: { layout: 'box', position: 'bottom center', equalWeightButtons: true, flipButtons: true },
            preferencesModal: { layout: 'box', equalWeightButtons: true },
        },
        categories: {
            necessary: { enabled: true, readOnly: true },
            analytics: {
                autoClear: { cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: /^_gat/ }] },
                services: { analytics_storage: { label: 'Analytics cookies' } },
            },
            functionality: {
                services: {
                    functionality_storage: { label: 'Remember optional preferences' },
                    personalization_storage: { label: 'Personalise content' },
                },
            },
        },
        language: {
            default: 'en',
            translations: {
                en: {
                    consentModal: {
                        title: 'Cookies, briefly.',
                        description: 'Essential cookies keep this site working. With your permission, we also use cookies for analytics and preferences. We measure successful form submissions and contact-link clicks without sending your form contents to Google Analytics. <button type="button" data-cc="show-preferencesModal" class="cc__link">Settings</button> · <a class="cc__link" href="/cookies/">Cookie policy</a>',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Decline',
                    },
                    preferencesModal: {
                        title: 'Your cookie preferences',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Decline optional',
                        savePreferencesBtn: 'Save preferences',
                        closeIconLabel: 'Close preferences',
                        sections: [
                            { description: 'Choose what you are comfortable with. Optional cookies stay off unless you accept them. You can change your mind at any time using Cookie settings.' },
                            { title: 'Essential', description: 'Required to keep the site secure, support core features and remember your cookie choices. These are always on.', linkedCategory: 'necessary' },
                            { title: 'Analytics', description: 'Allows Google Analytics to measure visits and how people use the site, so we can improve it.', linkedCategory: 'analytics' },
                            { title: 'Preferences', description: 'Allows optional features to remember your preferences and personalise content.', linkedCategory: 'functionality' },
                            { description: 'Read our <a class="cc__link" href="/cookies/">cookie policy</a> and <a class="cc__link" href="/privacy-policy/">privacy policy</a>, or <a class="cc__link" href="/contact/">contact us</a>.' },
                        ],
                    },
                },
            },
        },
    })

    const settings = document.createElement('button')
    settings.type = 'button'
    settings.className = 'avianese-cookie-settings'
    settings.textContent = 'Cookie settings'
    settings.addEventListener('click', () => CookieConsent.showPreferences())
    document.body.append(settings)

    const { measurementId } = window.avianeseConsent
    if (measurementId) {
        window.gtag('js', new Date())
        window.gtag('config', measurementId, {
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
            allow_enhanced_conversions: false,
        })
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
        document.head.append(script)
    }
}
